// API CALL
import * as React from 'react';
import { VendorEmails } from './VendorEmails';
import { NativeEmails } from './NativeEmails';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import type { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import { IndEmail } from '../../MedicareProAPI/Entities/Ind/IndEmail';
import { IndEmailRepository } from '../../MedicareProAPI/Repositories/Ind/IndEmailRepository';
import { AgencyDownlineRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyDownlineRepository';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import { StaffRepository } from '../../MedicareProAPI/Repositories/Staff/StaffRepository';
import { AgencyDownline } from '../../MedicareProAPI/Entities/Agency/AgencyDownline';
import type { AgencyDownlineCollection } from '../../MedicareProAPI/Collections/Agency/AgencyDownlineCollection';
import { IndRepository } from '../../MedicareProAPI/Repositories/Ind/IndRepository';
import { IndNoteRepository } from '../../MedicareProAPI/Repositories/Ind/IndNoteRepository';
import type { IndNote } from '../../MedicareProAPI/Entities/Ind/IndNote';
import { INTEGRATION_TYPE } from '../Constants/Integrations';

export interface EmailsCollectionProps {
  userAccessToken: string;
  individual: Ind;
  user: User;
  agencyId?: number;
  staffId?: number;
  onSetupClick?: (type: INTEGRATION_TYPE) => void;
}

/**
 * Wrapper component for Ind emails collection.
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2024-04-10
 * @lastUpdated
 * @version 1.1.0
 */
export const EmailsCollection = ({
  userAccessToken,
  individual,
  user,
  agencyId,
  staffId,
  onSetupClick,
}: EmailsCollectionProps): React.JSX.Element => {
  const individualRepository = new IndRepository();
  individualRepository.setAccessToken(userAccessToken);

  const individualEmailRepository = new IndEmailRepository();
  individualEmailRepository.setAccessToken(userAccessToken);

  const agencyDownlineRepository = new AgencyDownlineRepository();
  agencyDownlineRepository.setAccessToken(userAccessToken);

  const staffRepository = new StaffRepository();
  staffRepository.setAccessToken(userAccessToken);

  const individualNoteRepository = new IndNoteRepository();
  individualNoteRepository.setAccessToken(userAccessToken);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(userAccessToken);

  const [mailPlatform, setMailPlatform] = React.useState<string>();
  const [vendorMailPlatforms, setVendorMailPlatforms] = React.useState<Array<any>>([]);

  const [emails, setEmails] = React.useState<Array<string>>([]);
  const [emailsFetched, setEmailsFetched] = React.useState<boolean>(false);
  const [authorizedUsers, setAuthorizedUsers] = React.useState<Array<User>>([]);
  const [targetUser, setTargetUser] = React.useState<User>(null);
  const [targetUserName, setTargetUserName] = React.useState<string>(null);

  const [signature, setSignature] = React.useState<string>(user.getSignatureEmail());

  const [changingTargetUser, setChangingTargetUser] = React.useState<boolean>(false);

  const [noteEmails, setNoteEmails] = React.useState<Array<IndNote>>();

  const mailOptions = [
    {
      label: 'Outlook',
      value: 'microsoft',
    },
    {
      label: 'MPCRM',
      value: 'native',
    },
  ];

  const canUseThisPlatform: boolean =
    mailPlatform === 'native' ||
    (vendorMailPlatforms?.some((v) => v.reference === mailPlatform) ?? false);

  const handleTargetUserChange = (targetUser) => {
    const thisTargetUser = authorizedUsers.filter((user) => {
      return user.getUserId() == targetUser;
    });

    setTargetUser(thisTargetUser[0].getUserId());
    setTargetUserName(
      thisTargetUser[0].getUserFirstname() + ' ' + thisTargetUser[0].getUserLastname()
    );
    setChangingTargetUser(true);
  };

  const handleTemplateChange = async (selectedId: number) => {
    const template = targetUser
      .getLetters()
      .getItems()
      .find((t) => t.getLetterId() === selectedId);

    const individualData = await individualRepository.getInd(individual.getIndId(), {
      include: [
        'addresses',
        'phones',
        'emails',
        'spouse',
        'policies',
        'policies.company',
        'policies.plan',
      ],
    });

    const renderedBody = template.renderLetterBody(
      individualData,
      targetUser,
      targetUser.getSignature(),
      targetUser.getSignatureEmail(),
      targetUser.getSignaturePic(),
      null,
      false,
      'email',
      'single',
      null
    );

    const returnObject = {
      renderedBody: renderedBody,
      template: template,
    };

    return returnObject;
  };

  React.useEffect(() => {
    if (mailPlatform === 'native') {
      individualNoteRepository
        .getIndNotes(individual.getIndId(), { criteria: { indNoteType: 'email' } })
        .then((individualNotes) => {
          setNoteEmails(individualNotes.getItems());
        });
    }
  }, [mailPlatform]);

  /**
   * Gets external email platform of target user, if any
   */
  React.useEffect(() => {
    if (targetUser) {
      webAPI
        .getUserIntegrationFeatureAvailability(targetUser.getUserId(), 'email')
        .then((response) => {
          if (response[0]) {
            console.log('vendors', response);
            setVendorMailPlatforms(response);
            setMailPlatform(response[0].reference);
            // setMailPlatform('native');
            if (staffId) {
              staffRepository
                .getUserStaff(targetUser.getUserId(), {
                  include: ['staffEmailSignature'],
                  criteria: { staff: staffId },
                })
                .then((staffResponse) => {
                  if (staffResponse.getTotal() > 0) {
                    if (staffResponse.getItems()[0]['staffEmailSignature'] !== null) {
                      setSignature(staffResponse.getItems()[0].getSignatureEmail());
                    }
                  }
                });
            }
          } else {
            setMailPlatform('native');
            setChangingTargetUser(false);
          }
        });
    }
  }, [targetUser]);

  /**
   * Sets the target user or target users if Agency with LOA agents
   */
  React.useEffect(() => {
    if (individual) {
      individualEmailRepository.getIndEmails(individual.getIndId()).then((individualEmails) => {
        const emailArray = individualEmails.getItems().map((email) => email.getIndEmailAddress());
        setEmails(emailArray);
        setEmailsFetched(true);
      });
    }
    if (agencyId > 0) {
      agencyDownlineRepository
        .getAgencyDownlines(user.getUserId(), agencyId, {
          sortBy: 'downlineId',
          order: 'asc',
          include: ['emailSignature', 'letters'],
        })
        .then((response: AgencyDownlineCollection) => {
          if (response.getItems().length > 0) {
            const authorizedUsers = response
              .getItems()
              .filter((item, itemIndex) => item.getAgentStatus() === 'LOA' || itemIndex === 0)
              .map((item) => item.getUser());

            const thisTargetUser = authorizedUsers.filter((userResponse) => {
              return userResponse.getUserId() == user.getUserId();
            });

            setAuthorizedUsers(authorizedUsers);
            setTargetUser(thisTargetUser[0]);
            setTargetUserName(
              thisTargetUser[0].getUserFirstname() + ' ' + thisTargetUser[0].getUserLastname()
            );
          } else {
            setMailPlatform(null);
          }
        });
    } else {
      setTargetUser(user);
    }
  }, []);

  return (
    <>
      {mailPlatform !== undefined && (
        <select
          className="form-control form-control-sm"
          onChange={(e) => setMailPlatform(e.target.value)}
        >
          {mailOptions.map((item) => {
            return (
              <option value={item.value} selected={mailPlatform === item.value}>
                {item.label}
              </option>
            );
          })}
        </select>
      )}

      {mailPlatform === undefined && (
        <div className="col-12">
          <i className="fas fa-spinner fa-spin ml-2"></i>
        </div>
      )}
      {mailPlatform === null && authorizedUsers.length === 0 && (
        <div className="col-12">
          <div className="alert small alert-info" role="alert">
            No email account configured. Click{' '}
            <a href="https://www.medicareproapp.com/agent/settingsController/index/integration">
              here
            </a>{' '}
            to setup your email account.
          </div>
        </div>
      )}
      {mailPlatform === null && authorizedUsers.length > 0 && (
        <div className="col-12">
          <div className="alert small alert-info" role="alert">
            User has no email account configured.
          </div>

          <label className="label">Change Inbox</label>
          <select
            className="form-control form-control-sm mb-2"
            onChange={(e) => handleTargetUserChange(e.target.value)}
          >
            <option>Select...</option>
            {authorizedUsers.map((user) => {
              return (
                <option value={user.getUserId()}>
                  {user.getUserFirstname()} {user.getUserLastname()}
                </option>
              );
            })}
          </select>
        </div>
      )}
      {emailsFetched && mailPlatform !== undefined && (
        <>
          {mailPlatform === 'native' && noteEmails && (
            <NativeEmails
              noteEmails={noteEmails}
              emails={emails}
              userAccessToken={userAccessToken}
              targetUser={user}
              staffId={staffId}
              agencyId={agencyId}
              signature={signature}
              authorizedUsers={authorizedUsers}
              mailPlatform={mailPlatform}
              onTargetUserChange={handleTargetUserChange}
              onTemplateChange={handleTemplateChange}
            />
          )}

          {mailPlatform !== 'native' && canUseThisPlatform && (
            <VendorEmails
              emails={emails}
              userAccessToken={userAccessToken}
              targetUser={user}
              staffId={staffId}
              agencyId={agencyId}
              signature={signature}
              authorizedUsers={authorizedUsers}
              mailPlatform={mailPlatform}
              onTargetUserChange={handleTargetUserChange}
              onTemplateChange={handleTemplateChange}
            />
          )}

          {mailPlatform !== 'native' && !canUseThisPlatform && (
            <div
              className="badge badge-pill badge-info cp mt-2"
              style={{ fontSize: '8pt' }}
              onClick={() => onSetupClick(INTEGRATION_TYPE.EMAIL)}
            >
              <i className="far fa-plus-circle"></i> Add/Edit an integration
            </div>
          )}
        </>
      )}
    </>
  );
};
